import Rollbar from 'rollbar';

import { ClientErrorReportLevel } from '@/enums/ClientErrorReportLevel';
import type { ClientObservabilityConfig, ReportClientErrorArgs } from '@/interfaces/ClientObservability';
import { getWindowOrNull } from '@/lib/browser/runtime';
import { normalizeRuntimeEnvironmentValue } from '@/lib/observability/runtime';
import { DEFAULT_ROLLBAR_SCRUB_FIELDS } from '@/lib/observability/shared';

type Dictionary = Record<string, unknown>;

const ROLLBAR_URL_FIELDS = ['from', 'href', 'referrer', 'to', 'url'] as const;

const asDictionary = (value: unknown): Dictionary | null =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Dictionary) : null;

const stripUrlFragment = (value: unknown) => {
    if (typeof value !== 'string') {
        return value;
    }

    const fragmentIndex = value.indexOf('#');

    return fragmentIndex === -1 ? value : value.slice(0, fragmentIndex);
};

const stripFragmentsFromUrlFields = (value: unknown) => {
    const record = asDictionary(value);

    if (!record) {
        return;
    }

    for (const field of ROLLBAR_URL_FIELDS) {
        record[field] = stripUrlFragment(record[field]);
    }
};

const stripFragmentsFromRollbarPayload = (payload: unknown) => {
    const record = asDictionary(payload);

    if (!record) {
        return;
    }

    stripFragmentsFromUrlFields(record.request);

    const body = asDictionary(record.body);
    const telemetry = body?.telemetry;

    if (!Array.isArray(telemetry)) {
        return;
    }

    for (const telemetryEvent of telemetry) {
        stripFragmentsFromUrlFields(asDictionary(telemetryEvent)?.body);
    }
};

declare global {
    interface Window {
        __CHATTENGER_CLIENT_ROLLBAR__?: Rollbar | null;
        __CHATTENGER_OBSERVABILITY__?: ClientObservabilityConfig;
    }
}

const normalizeConfig = (value: unknown): ClientObservabilityConfig | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const raw = value as Record<string, unknown>;
    const token = String(raw.rollbarClientToken || '').trim();
    const environment = normalizeRuntimeEnvironmentValue(raw.environment);
    const buildMarker = String(raw.buildMarker || '').trim();

    if (!environment) {
        return null;
    }

    return {
        rollbarClientToken: token || null,
        environment,
        buildMarker: buildMarker || null,
    };
};

export const readClientObservabilityConfig = (): ClientObservabilityConfig | null => {
    const browserWindow = getWindowOrNull();
    if (!browserWindow) {
        return null;
    }

    return normalizeConfig(browserWindow.__CHATTENGER_OBSERVABILITY__);
};

export const getClientRollbar = (): Rollbar | null => {
    const browserWindow = getWindowOrNull();
    if (!browserWindow) {
        return null;
    }

    if (typeof browserWindow.__CHATTENGER_CLIENT_ROLLBAR__ !== 'undefined') {
        return browserWindow.__CHATTENGER_CLIENT_ROLLBAR__ ?? null;
    }

    const config = readClientObservabilityConfig();
    if (!config?.rollbarClientToken) {
        browserWindow.__CHATTENGER_CLIENT_ROLLBAR__ = null;
        return null;
    }

    const instance = new Rollbar({
        accessToken: config.rollbarClientToken,
        autoInstrument: {
            navigation: false,
            networkRequestBody: false,
        },
        captureUncaught: true,
        captureUnhandledRejections: true,
        codeVersion: config.buildMarker || undefined,
        environment: config.environment,
        filterTelemetry: (event) => event.type === 'navigation',
        scrubFields: [...DEFAULT_ROLLBAR_SCRUB_FIELDS],
        transform: (data, item) => {
            stripFragmentsFromRollbarPayload(data);
            stripFragmentsFromRollbarPayload(item);
        },
        payload: {
            client: {
                javascript: {
                    source_map_enabled: true,
                    code_version: config.buildMarker || undefined,
                },
            },
            custom: {
                buildMarker: config.buildMarker || undefined,
                runtimeEnvironment: config.environment,
                service: 'chattenger_nextjs',
                surface: 'client',
            },
        },
    });

    browserWindow.__CHATTENGER_CLIENT_ROLLBAR__ = instance;
    return instance;
};

export const reportClientError = ({ error, message, extra = {}, level = ClientErrorReportLevel.ERROR }: ReportClientErrorArgs) => {
    const rollbar = getClientRollbar();
    if (!rollbar) {
        return;
    }

    const payload = {
        ...extra,
        runtimeEnvironment: readClientObservabilityConfig()?.environment || null,
        service: 'chattenger_nextjs',
        source: 'nextjs-client',
    };

    const candidate = error instanceof Error ? error : typeof message === 'string' && message ? message : 'Client error';

    if (level === ClientErrorReportLevel.CRITICAL) {
        rollbar.critical(candidate, payload);
        return;
    }

    if (level === ClientErrorReportLevel.WARNING) {
        rollbar.warning(candidate, payload);
        return;
    }

    rollbar.error(candidate, payload);
};
